/**
 * High-Level Data Initialization Utility
 *
 * Initializes high_level_options and high_level_choices for configuration templates.
 * This utility can be used on both client and server to ensure these properties
 * are always present when needed.
 */

import * as changeCase from 'change-case'
import { getIntersection } from './getIntersection'
import { PREFERRED_PRINTING_PROCESSES } from './printingProcess'
import { isNotNullish } from './isNullish'
import { ensureArray } from './ensureArray'
import type { Color, Print } from '~~/layers/base/types/productPartConfiguration'
import type {
    HighLevelChoices,
    HighLevelOptions,
    ProductPart,
    PartElement,
} from '~~/layers/base/types/designProject'

/**
 * Template type that accepts both camelCase (client) and snake_case (database) formats.
 * Used for functions that need to work with templates from both sources.
 * This type is intentionally permissive to accept templates in either format.
 */
export type FlexibleConfigurationTemplate = Record<string, unknown> & {
    base_structure?: unknown
    baseStructure?: unknown
    high_level_options?: HighLevelOptions | unknown
    highLevelOptions?: HighLevelOptions
    high_level_choices?: HighLevelChoices | unknown
    highLevelChoices?: HighLevelChoices
}

/**
 * Sorts printing processes to match the preferred order defined in PREFERRED_PRINTING_PROCESSES.
 * Filters out any processes that don't exist in the provided array.
 */
export function sortPrintingProcessesByPreferred(printingProcesses: Print[]): Print[] {
    return PREFERRED_PRINTING_PROCESSES.map((preferredPrintProcess) =>
        printingProcesses.find(
            (printProcess) => printProcess.processId === preferredPrintProcess.processId
        )
    ).filter(isNotNullish)
}

/**
 * Derives printing process options from base structure elements.
 * Handles both camelCase (baseStructure) and snake_case (base_structure) formats.
 * Also handles Print objects in both snake_case and camelCase formats.
 * Matches the original logic from getPrintProcessProperties in syncConfigurationTemplates.ts.
 */
function derivePrintingProcessOptions(template: FlexibleConfigurationTemplate): Print[] {
    // Handle both camelCase and snake_case formats
    const baseStructure =
        (template.baseStructure as ProductPart[] | undefined) ||
        (template.base_structure as ProductPart[] | undefined) ||
        []
    const allPrintingOptions: Print[] = []
    const seenProcessIds = new Set<number>()

    baseStructure.forEach((part: ProductPart) => {
        if (part.elements && Array.isArray(part.elements)) {
            part.elements.forEach((element: PartElement) => {
                // Skip paper and liner elements (matching original logic)
                if (element.type === 'paper' || element.type === 'liner') {
                    return
                }

                // Collect from both prints array and element.print (singular)
                const printsToCheck: (
                    | Print
                    | { process_id?: number; processId?: number; name?: string; shortName?: string }
                )[] = []

                if (element.prints && Array.isArray(element.prints)) {
                    printsToCheck.push(...element.prints)
                }

                if (element.print) {
                    printsToCheck.push(element.print)
                }

                printsToCheck.forEach((print) => {
                    // Handle both snake_case and camelCase formats
                    const processId =
                        print.processId ?? (print as { process_id?: number }).process_id
                    const name = print.name ?? ''
                    const shortName =
                        print.shortName ?? (print as { short_name?: string }).short_name ?? ''

                    if (processId !== undefined && !seenProcessIds.has(processId)) {
                        seenProcessIds.add(processId)
                        allPrintingOptions.push({
                            processId,
                            name,
                            shortName,
                        })
                    }
                })
            })
        }
    })

    return sortPrintingProcessesByPreferred(allPrintingOptions)
}

/**
 * Derives color options from intersection of all element colors.
 * Handles both camelCase (baseStructure) and snake_case (base_structure) formats.
 */
function deriveColorOptions(template: FlexibleConfigurationTemplate): Color[] {
    // Handle both camelCase and snake_case formats
    const baseStructure =
        (template.baseStructure as ProductPart[] | undefined) ||
        (template.base_structure as ProductPart[] | undefined) ||
        []
    const allElementColors: Color[][] = []

    baseStructure.forEach((part: ProductPart) => {
        if (part.elements && Array.isArray(part.elements)) {
            part.elements.forEach((element: PartElement) => {
                if (element.colors && Array.isArray(element.colors)) {
                    allElementColors.push(element.colors)
                }
            })
        }
    })

    // Get intersection of all color arrays (colors that appear in all elements)
    return getIntersection(allElementColors, (color, color2) => color.cid === color2.cid)
}

/**
 * Derives default selected colors from element colors in the base structure.
 * Only includes colors that are present in the available color options.
 * Handles both camelCase (baseStructure) and snake_case (base_structure) formats.
 */
function deriveDefaultColors(
    template: FlexibleConfigurationTemplate,
    availableColors: Color[]
): Color[] {
    // Handle both camelCase and snake_case formats
    const baseStructure =
        (template.baseStructure as ProductPart[] | undefined) ||
        (template.base_structure as ProductPart[] | undefined) ||
        []

    // Get all element colors from the base structure
    const mainColors = baseStructure
        .map((part: ProductPart) => part?.elements?.map((e: PartElement) => e.color))
        .filter((colors): colors is (Color | null | undefined)[] => !!colors)
        .flat()

    if (!mainColors || mainColors.length === 0) {
        return []
    }

    // Filter to only include colors that are in the available color options
    const defaultColors = ensureArray(mainColors)
        .map((colorObj: Color | null | undefined) =>
            colorObj ? availableColors.find((color) => color.cid === colorObj?.cid) : undefined
        )
        .filter(isNotNullish)
        .filter((color, i, arr) => arr.findIndex((c) => c.cid === color.cid) === i)

    return defaultColors
}

/**
 * Gets all envelope parts that have a return address element.
 * Handles both camelCase (baseStructure) and snake_case (base_structure) formats.
 * Returns an array of parts with their constructed fieldset paths.
 */
function getEnvelopePartsWithReturnAddress(
    template: FlexibleConfigurationTemplate
): Array<{ part: ProductPart; fieldsetPath: string }> {
    // Handle both camelCase and snake_case formats
    const baseStructure =
        (template.baseStructure as ProductPart[] | undefined) ||
        (template.base_structure as ProductPart[] | undefined) ||
        []

    return baseStructure
        .filter((part: ProductPart) => {
            // Check if part name contains "envelope" (case-insensitive)
            const partNameLower = part.name.toLowerCase()
            if (!partNameLower.includes('envelope')) {
                return false
            }

            // Check if this part has a return address element
            return part.elements?.some(
                (element: PartElement) => changeCase.snakeCase(element.name) === 'return_address'
            )
        })
        .map((part: ProductPart) => {
            // Construct the fieldset path: camelCase(partName)ReturnAddress
            // This matches the pattern used in personalizeForm.store.ts where
            // path = changeCase.camelCase(`${formPart.id}_${fieldset.id}`)
            const partNameCamel = changeCase.camelCase(part.name)
            const fieldsetPath = `${partNameCamel}ReturnAddress`

            return {
                part,
                fieldsetPath,
            }
        })
}

/**
 * Initializes high_level_options and high_level_choices for a configuration template.
 *
 * This function derives the options from the base structure if they don't already exist,
 * ensuring that these properties are always present when needed.
 *
 * Works with both camelCase (baseStructure) and snake_case (base_structure) formats
 * to support both client and server usage. Sets properties in both formats to ensure
 * compatibility regardless of which format the template uses.
 *
 * @param template - The configuration template to initialize (accepts both camelCase and snake_case formats)
 * @returns The template with initialized high_level_options and high_level_choices
 */
export function initializeHighLevelData(
    template: FlexibleConfigurationTemplate
): FlexibleConfigurationTemplate {
    // Check if high_level_options already exists in either format
    const existingOptions =
        (template.highLevelOptions as HighLevelOptions | undefined) ||
        (template.high_level_options as HighLevelOptions | undefined)

    // Re-derive if options don't exist OR if prints array is missing/empty
    // This handles edge cases where existing projects have high_level_options but missing prints
    const needsInitialization =
        !existingOptions || !existingOptions.prints || existingOptions.prints.length === 0

    if (needsInitialization) {
        // Preserve existing colors if they exist, otherwise derive them
        const colors = existingOptions?.colors || deriveColorOptions(template)
        const prints = derivePrintingProcessOptions(template)

        const highLevelOptions = {
            colors,
            prints,
        } as HighLevelOptions

        // Set in both formats for compatibility
        template.highLevelOptions = highLevelOptions
        template.high_level_options = highLevelOptions
    }

    // Check if high_level_choices already exists in either format
    const hasHighLevelChoices = template.highLevelChoices || template.high_level_choices

    if (!hasHighLevelChoices) {
        // Get prints from highLevelOptions (which should be initialized by now)
        const highLevelOptions =
            (template.highLevelOptions as HighLevelOptions | undefined) ||
            (template.high_level_options as HighLevelOptions | undefined)
        const prints = highLevelOptions?.prints || []
        const autoSelectedPrint = prints.length === 1 ? prints[0] : null

        // Derive default colors from element colors in the base structure
        const availableColors = highLevelOptions?.colors || []
        const defaultColors = deriveDefaultColors(template, availableColors)

        // Set default fieldsetsHidden to show return address for all envelope parts that have it
        const fieldsetsHidden: { [fieldsetPath: string]: 'show' | 'hide' } = {}
        const envelopePartsWithReturnAddress = getEnvelopePartsWithReturnAddress(template)
        envelopePartsWithReturnAddress.forEach(({ fieldsetPath }) => {
            fieldsetsHidden[fieldsetPath] = 'show'
        })

        const highLevelChoices = {
            colors: defaultColors,
            print: autoSelectedPrint,
            additionalColorsAdded: false,
            fieldsetsHidden,
        } as HighLevelChoices

        // Set in both formats for compatibility
        template.highLevelChoices = highLevelChoices
        template.high_level_choices = highLevelChoices
    }

    return template
}
